import React, { Component } from 'react';
import Page from 'app/components/page/Page';
import { Box, Flex, Heading, Text } from 'core/components';
import { TOOL_CALL_STATUS } from 'shared/kaizen/constants';
import AgentMessage from './components/AgentMessage';
import KaizenToolResultPanel from './KaizenToolResultPanel';
import { TOOL_NAMES } from './ToolResultFactory';

/**
 * Internal debug / visual-regression harness for the AI Advisor (Kaizen) conversation
 * thread. It renders every distinct visual state of `AgentMessage` / `StepComponent`
 * side-by-side so styling can be reviewed for consistency in one place.
 *
 * Route: /v4/core/ai-advisor/debug (sudo only — see core/index.js)
 */

const BASE_TIME = '2026-06-01T12:00:00Z';

// Minimal stub standing in for the chat session model. Only `id` is read by the
// tool-call cards (auto-approval cookie lookup); approval/explorer actions are no-ops here.
const sessionModelStub = { id: 'kaizen-debug-session' };

let toolSeq = 0;
function makeToolCall(overrides = {}) {
  toolSeq += 1;
  return {
    id: `debug-tool-${toolSeq}`,
    tool_tracking_id: `debug-track-${toolSeq}`,
    tool_name: TOOL_NAMES.WEB_SEARCH,
    agent_name: 'kaizen',
    title: 'Tool call',
    status: TOOL_CALL_STATUS.SUCCESS,
    ...overrides
  };
}

let stepSeq = 0;
function makeStep(overrides = {}) {
  stepSeq += 1;
  return {
    id: `debug-step-${stepSeq}`,
    step_number: stepSeq,
    processing_status: 'COMPLETED',
    llm_response: {},
    toolCalls: [],
    ...overrides
  };
}

let turnSeq = 0;
function makeTurn(steps, overrides = {}) {
  turnSeq += 1;
  return {
    id: `debug-turn-${turnSeq}`,
    created_at: BASE_TIME,
    status: 'COMPLETED',
    feedback: [],
    steps,
    ...overrides
  };
}

// Each entry: { label, description, turn }. Order groups related states together.
function buildFixtures() {
  return [
    {
      label: 'Reasoning only',
      description: 'A step with reasoning and no final answer — collapsible reasoning block.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: {
            reasoning:
              'I need to determine which devices are reporting the most traffic. ' +
              'Let me start by querying the data explorer for the top talkers over the last hour.'
          }
        })
      ])
    },
    {
      label: 'Final answer + feedback controls',
      description: 'Completed step with a final answer, copy button, and thumbs up/down feedback.',
      turn: makeTurn([
        makeStep({
          llm_response: {
            final_answer:
              '### Summary\n\nYour top traffic source over the last hour is **device-core-01** ' +
              'at 4.2 Gbps, followed by **device-edge-03** at 2.1 Gbps.\n\n' +
              '- Total monitored: 24 devices\n- Anomalies detected: none'
          }
        })
      ])
    },
    {
      label: 'Tool call — pending',
      description: 'A tool call still running (spinner, muted title).',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [makeToolCall({ title: 'Searching the web…', status: undefined })]
        })
      ])
    },
    {
      label: 'Tool call — success (interactive)',
      description: 'A completed tool with a detail view. Click to open it in the right panel.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.WEB_SEARCH,
              title: 'Web search: BGP route leak best practices',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        })
      ])
    },
    {
      label: 'Tool call — failed',
      description: 'A tool call that errored.',
      turn: makeTurn([
        makeStep({
          toolCalls: [makeToolCall({ title: 'Query device inventory', status: TOOL_CALL_STATUS.FAILED })]
        })
      ])
    },
    {
      label: 'Tool call — user rejected',
      description: 'A tool call the user declined to approve.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({ title: 'Run device command: show config', status: TOOL_CALL_STATUS.USER_REJECTED })
          ]
        })
      ])
    },
    {
      label: 'Tool call — approval timeout',
      description: 'A tool call whose approval request timed out.',
      turn: makeTurn([
        makeStep({
          toolCalls: [makeToolCall({ title: 'Run device command: reload', status: TOOL_CALL_STATUS.APPROVAL_TIMEOUT })]
        })
      ])
    },
    {
      label: 'Tool call — awaiting approval',
      description: 'A tool call paused, showing the approve/reject confirmation actions.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.DEVICE_COMMAND,
              title: 'Run device command: show interfaces',
              status: TOOL_CALL_STATUS.AWAITING_APPROVAL
            })
          ]
        })
      ])
    },
    {
      label: 'Tool call — approved by user',
      description: 'A completed tool that required and received user approval.',
      turn: makeTurn([
        makeStep({
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.DEVICE_COMMAND,
              title: 'Run device command: show interfaces',
              status: TOOL_CALL_STATUS.USER_APPROVED,
              approved_by_user_name: 'Dušan Pajin'
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent delegation — waiting (Math Agent)',
      description: 'A delegation tool still running; surfaces "Waiting for Math Agent to respond…".',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [
            makeToolCall({ tool_name: TOOL_NAMES.MATH_AGENT, title: 'Delegate to Math Agent', status: undefined })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent delegation — waiting (Synthetics Agent)',
      description: 'A delegation tool still running; surfaces "Waiting for Synthetics Agent to respond…".',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_MONITORING_AGENT,
              title: 'Delegate to Synthetics Agent',
              status: undefined
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent group — processing',
      description: 'Inline summary card for a sub-agent whose tool calls are still running. Click to open transcript.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            }),
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get test results',
              status: undefined
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent group — completed',
      description: 'Inline summary card for a sub-agent whose tool calls all completed. Click to open transcript.',
      turn: makeTurn([
        makeStep({
          llm_response: { reasoning: 'Delegating the synthetics question to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            }),
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get test results',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent step group — processing',
      description: 'Summary card for consecutive sub-agent steps that are still running.',
      turn: makeTurn([
        makeStep({
          is_sub_agent_step: true,
          agent_name: 'synthetics_agent',
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'Checking synthetic test health.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        }),
        makeStep({
          is_sub_agent_step: true,
          agent_name: 'synthetics_agent',
          processing_status: 'PROCESSING',
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TRACE_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get trace results',
              status: undefined
            })
          ]
        })
      ])
    },
    {
      label: 'Sub-agent step group — completed',
      description: 'Summary card for consecutive completed sub-agent steps.',
      turn: makeTurn([
        makeStep({
          is_sub_agent_step: true,
          agent_name: 'synthetics_agent',
          llm_response: { reasoning: 'Checking synthetic test health.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        }),
        makeStep({
          is_sub_agent_step: true,
          agent_name: 'synthetics_agent',
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS_TRACE_RESULTS,
              agent_name: 'synthetics_agent',
              title: 'Get trace results',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        })
      ])
    },
    {
      label: 'Mixed: reasoning + sub-agent + final answer',
      description: 'A realistic multi-step turn combining a main step, a sub-agent group, and a final answer.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'This needs synthetics data, so I will delegate to the Synthetics Agent.' },
          toolCalls: [
            makeToolCall({
              tool_name: TOOL_NAMES.SYNTHETICS,
              agent_name: 'synthetics_agent',
              title: 'Get synthetic test status',
              status: TOOL_CALL_STATUS.SUCCESS
            })
          ]
        }),
        makeStep({
          llm_response: {
            final_answer: 'All 12 synthetic tests are passing. The slowest is **us-east → eu-west** at 142 ms.'
          }
        })
      ])
    },
    {
      label: 'Turn — cancelled',
      description: 'A turn the user cancelled mid-response.',
      turn: makeTurn(
        [makeStep({ llm_response: { reasoning: 'Starting to gather data…' }, processing_status: 'PROCESSING' })],
        { status: 'CANCELLED' }
      )
    },
    {
      label: 'Turn — error',
      description: 'A turn that failed with a turn-level error.',
      turn: makeTurn([makeStep({ llm_response: { reasoning: 'Attempting to query…' } })], {
        error_message: 'Internal error while processing the request.'
      })
    },
    {
      label: 'Step — error',
      description: 'A step-level error callout.',
      turn: makeTurn([
        makeStep({
          processing_status: 'PROCESSING',
          llm_response: { reasoning: 'Querying device inventory…' },
          error_message: 'TOOL_TIMEOUT'
        })
      ])
    }
  ];
}

class KaizenDebugView extends Component {
  state = {
    selectedToolResults: [],
    stepReasoningOpenState: {}
  };

  fixtures = buildFixtures();

  formatDate = () => 'Jun 01, 2026, 12:00 pm';

  handleToggleReasoning = (stepId) => {
    this.setState((prevState) => ({
      stepReasoningOpenState: {
        ...prevState.stepReasoningOpenState,
        [stepId]: !(prevState.stepReasoningOpenState[stepId] ?? true)
      }
    }));
  };

  handleSelectToolResult = (tool) => {
    this.setState((prevState) => {
      const toolExists = prevState.selectedToolResults.some((existing) => existing.id === tool.id);
      if (toolExists) {
        return null;
      }
      return { selectedToolResults: [...prevState.selectedToolResults, tool] };
    });
  };

  handleCloseToolResult = (tool) => {
    this.setState((prevState) => ({
      selectedToolResults: prevState.selectedToolResults.filter((t) => t.id !== tool.id)
    }));
  };

  handleCloseToolResults = () => {
    this.setState({ selectedToolResults: [] });
  };

  handleNoop = () => {};

  render() {
    const { selectedToolResults, stepReasoningOpenState } = this.state;

    return (
      <Page title="AI Advisor — Component States (Debug)" scrolls>
        <Flex height="100%" width="100%" overflow="hidden">
          <Box flex={1} overflow="auto" p={3}>
            <Heading level={3} mb={1}>
              AI Advisor — Conversation Component States
            </Heading>
            <Text muted as="p" mb={3}>
              Every distinct visual state of <code>AgentMessage</code> / <code>StepComponent</code>, rendered together
              for style review. Interactive cards open their details in the right panel.
            </Text>

            <Flex flexDirection="column" gap={4}>
              {this.fixtures.map(({ label, description, turn }) => (
                <Box key={turn.id} borderBottom="thin" pb={3}>
                  <Heading level={5} mb={0}>
                    {label}
                  </Heading>
                  <Text small muted as="p" mb={2}>
                    {description}
                  </Text>
                  <Box bg="decks.background" p={2} borderRadius={4}>
                    <AgentMessage
                      turn={turn}
                      stepReasoningOpenState={stepReasoningOpenState}
                      onToggleReasoning={this.handleToggleReasoning}
                      onSelectToolResult={this.handleSelectToolResult}
                      onSendToExplorer={this.handleNoop}
                      onShowAgentDataInSidebar={this.handleNoop}
                      formatDate={this.formatDate}
                      isCopilotMode={false}
                      isExistingChatLoading={false}
                      sessionModel={sessionModelStub}
                    />
                  </Box>
                </Box>
              ))}
            </Flex>
          </Box>

          {selectedToolResults.length > 0 && (
            <Box width={480} borderLeft="thin" overflow="hidden">
              <KaizenToolResultPanel
                tools={selectedToolResults}
                onCloseToolResult={this.handleCloseToolResult}
                onClose={this.handleCloseToolResults}
                onSelectToolResult={this.handleSelectToolResult}
                sessionModel={sessionModelStub}
              />
            </Box>
          )}
        </Flex>
      </Page>
    );
  }
}

export default KaizenDebugView;
